import { types as api } from '@mesa-labs/mesa-api';
import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  unwrapResult,
} from '@reduxjs/toolkit';
import QRCode from 'qrcode';
import { redirect } from 'react-router-dom';

import { Partners, UserAccountActivityType } from '@mesa-labs/mesa-api/dist/types';
import { removeAnalyticsGroup, reset, setAnalyticsUserId } from '../../analytics';
import cognitoIdentityProvider from '../../cognito';
import { vendorApi } from '../api/vendor';
import { RootState } from '../store';
import { updateOnboardingId } from './onboarding';
import * as types from './types';

export const ATTRIBUTE_MFA_METHOD = 'custom:mfaMethod';

export enum MfaMethod {
  SOFTWARE_AUTH = 'Software Authenticator',
  SMS = 'SMS',
}

export enum AuthenticationGroup {
  OPERATORS = 'Operators',
  FACILITATORS = 'Facilitators',
  FINANCIERS = 'Financiers',
  VENDORS = 'Vendors',
  VENDOR_MANAGEMENT = 'VendorManagement',
}

export interface AuthState {
  externalVendorId?: string;
  vendorId?: string;
  email?: string;
  mfaCode: string;
  smsCode: string;
  error?: Error;
  isLoggedIn: boolean;
  loading: boolean;
  qrCodeUrl?: string;
  mfaMethod?: MfaMethod;
  roles?: AuthenticationGroup[];
  isReadOnly: boolean;
  phoneNumber?: string;
  timeout429Timestamp?: number;
}

type ChangePasswordArgs = {
  oldPassword: string;
  newPassword: string;
};

const initialState: AuthState = {
  externalVendorId: undefined,
  email: undefined,
  mfaCode: '',
  smsCode: '',
  error: undefined,
  isLoggedIn: false,
  loading: false,
  vendorId: undefined,
  qrCodeUrl: undefined,
  roles: [],
  isReadOnly: true,
  timeout429Timestamp: 0,
};

export const confirmSignUp = createAsyncThunk(
  'auth/confirmAccount',
  async (args: { email: string, code: string }): Promise<void> => {
    await cognitoIdentityProvider.confirmAccount(args.email, args.code);
  },
);

export const resendConfirmationCode = createAsyncThunk(
  'auth/resendConfirmationCode',
  async (args: { email: string }): Promise<void> => {
    await cognitoIdentityProvider.resendConfirmationCode(args.email);
  },
);

export const postSignIn = createAsyncThunk<{
  vendorId: string;
  mfaMethod: MfaMethod;
  isReadOnly: boolean;
  referralCode?: api.ProgramCodes,
  skipParentIdCheck?: boolean;
  email: string;
}, {
  onboardingId?: string;
  initialSignIn?: boolean;
  email: string;
  promoCode?: api.PromoCodes;
  referralCode?: api.ProgramCodes;
  subdomain?: string;
  skipParentIdCheck?: boolean;
}>(
  'auth/postSignIn',
  async (args: {
    onboardingId?: string,
    initialSignIn?: boolean,
    email: string,
    promoCode?: api.PromoCodes,
    referralCode?: api.ProgramCodes,
    subdomain?: string,
    skipParentIdCheck?: boolean;
  }, thunkApi): Promise<{ vendorId: string; mfaMethod: MfaMethod; isReadOnly: boolean, referralCode?: api.ProgramCodes, email: string }> => {
    let attributes: Record<string, any> = {};

    let getUserAccountResult;

    if (typeof args.skipParentIdCheck === 'undefined' || !args.skipParentIdCheck) {
      getUserAccountResult = await thunkApi.dispatch(
        vendorApi.endpoints.getUserAccount.initiate({ email: args.email }),
      );
    }

    /** Initial user account creation */
    if (args.onboardingId) {
      const registerOnboardingResult = await thunkApi.dispatch(
        vendorApi.endpoints.registerOnboarding.initiate({ onboardingId: args.onboardingId }),
      );

      if ('error' in registerOnboardingResult) {
        await cognitoIdentityProvider.signOut();

        throw new Error('There was an error setting up your account. Please contact support@joinmesa.com for assistance.');
      }

      await cognitoIdentityProvider.refreshCurrentSession();

      await thunkApi.dispatch(updateOnboardingId(''));
    } else if (args.initialSignIn && !getUserAccountResult?.data?.parentId) {
      /** Create initial user account without association to an onboarding, but only if this is not a child account (no parentId set) */
      await thunkApi.dispatch(
        vendorApi.endpoints.createInitialUserAccount.initiate({ email: args.email, readOnly: false }),
      );
    }

    /** Add initial onboardingData on the userAccount if initial sign in and not a child account (no parentId set) */
    if (args.initialSignIn && !getUserAccountResult?.data?.parentId) {
      await thunkApi.dispatch(
        vendorApi.endpoints.addUserAccountOnboardingData.initiate({
          email: args.email,
          onboardingData: {
            promoCode: args.promoCode,
            referralCode: args.referralCode,
            subdomain: args.subdomain,
          },
        }),
      );
    }

    /** Child Account registration */
    let user = cognitoIdentityProvider.getCurrentUser();
    attributes = user?.getAttributes() || {};

    let vendorId = attributes['custom:vendorId'];
    const mfaMethod = attributes[ATTRIBUTE_MFA_METHOD] as MfaMethod;

    if (!vendorId && attributes.email) {
      // register child account if a parentId exists
      if (getUserAccountResult?.data?.parentId) {
        const registerUserAccountResult = await thunkApi.dispatch(
          vendorApi.endpoints.registerUserAccount.initiate({ email: attributes.email }),
        );

        if ('error' in registerUserAccountResult) {
          await cognitoIdentityProvider.signOut();

          throw new Error('There was an error setting up your account. Please contact support@joinmesa.com for assistance.');
        }

        await cognitoIdentityProvider.refreshCurrentSession();
        user = cognitoIdentityProvider.getCurrentUser();
        attributes = user?.getAttributes() || {};

        vendorId = attributes['custom:vendorId'];
      }
    }

    return {
      vendorId,
      mfaMethod,
      isReadOnly: attributes['custom:readOnly'] === 'true',
      referralCode: args.referralCode || getUserAccountResult?.data?.onboardingData?.referralCode,
      email: attributes.email,
    };
  },
);

export const signIn = createAsyncThunk<{
  vendorId: string;
  mfaMethod: MfaMethod;
  isReadOnly: boolean;
  email: string;
}, {
  email: string;
  password: string;
  onboardingId?: string;
  totpCallback: any;
  smsCodeCallback: () => Promise<string>;
  initialSignIn?: boolean;
  promoCode?: api.PromoCodes;
  referralCode?: api.ProgramCodes;
  subdomain?: string;
  skipParentIdCheck?: boolean;
}>(
  'auth/signIn',
  async (args: {
    email: string,
    password: string,
    onboardingId?: string | undefined,
    totpCallback: () => Promise<string>,
    smsCodeCallback: () => Promise<string>,
    initialSignIn?: boolean,
    promoCode?: api.PromoCodes,
    referralCode?: api.ProgramCodes,
    subdomain?: string,
    skipParentIdCheck?: boolean,
  }, thunkApi): Promise<{ vendorId: string; mfaMethod: MfaMethod; isReadOnly: boolean; email: string }> => {
    const {
      email,
      password,
      onboardingId,
      totpCallback,
      smsCodeCallback,
      initialSignIn,
      promoCode,
      referralCode,
      subdomain,
      skipParentIdCheck,
    } = args;

    // clear any prior session first
    await cognitoIdentityProvider.signOut();
    await cognitoIdentityProvider.signIn(email, password, totpCallback, smsCodeCallback);

    const session = cognitoIdentityProvider.getCurrentSession();
    const sessionPayload = session?.getIdToken().decodePayload() || {};
    const cognitoUsername = sessionPayload['cognito:username'] as string | undefined;

    // set GA userId config property to the cognitoUsername
    if (cognitoUsername) {
      setAnalyticsUserId(cognitoUsername);
    }

    const postSignInResult = unwrapResult(await thunkApi.dispatch(postSignIn({ onboardingId, initialSignIn, email, promoCode, referralCode, subdomain, skipParentIdCheck })));

    // vendorId and email are only known once postSignIn resolves them (from Cognito attributes / account registration)
    const { vendorId, email: authenticatedEmail } = postSignInResult;

    // the caller may have signed in with a Cognito username rather than an email (e.g. demo mode);
    // the activity endpoint is keyed on email, so log with the email Cognito actually resolved
    const activityEmail = authenticatedEmail || email;

    cognitoIdentityProvider.subscribe((event) => {
      if (event === 'refresh_session' && vendorId) {
        thunkApi.dispatch(vendorApi.endpoints.logUserAccountActivity.initiate({ email: activityEmail, activity: UserAccountActivityType.AuthRefresh })).catch(() => { /* */ });
      }
    });

    if (vendorId) {
      thunkApi.dispatch(vendorApi.endpoints.logUserAccountActivity.initiate({ email: activityEmail, activity: UserAccountActivityType.AuthSignIn })).catch(() => { /* */ });
    }

    return postSignInResult;
  },
);

export const signUp = createAsyncThunk(
  'auth/signUp',
  async (args: {
    email: string,
    password: string,
    trackingPartnerId?: Partners,
    onboardingId?: string,
    promoCode?: api.PromoCodes,
    referralCode?: api.ProgramCodes,
    subdomain?: string,
  }, { dispatch }): Promise<void> => {
    // clear any prior session first
    await cognitoIdentityProvider.signOut();

    // On signUp, attempt to auto signIn if the 'confirm_account' event is dispatched
    // by our cognitoProvider. Depending on if the vendor is created (vendorId) is set,
    // the user is either redirected to the onboarding flow (no vendorId) or to the dashboard
    // (vendorId, therefore a child account).
    cognitoIdentityProvider.subscribe(async (evt) => {
      if (evt === 'confirm_account') {
        const result = await dispatch(signIn({
          email: args.email,
          password: args.password,
          totpCallback: () => Promise.resolve(''),
          smsCodeCallback: () => Promise.resolve(''),
          initialSignIn: true,
          onboardingId: args.onboardingId,
          promoCode: args.promoCode,
          referralCode: args.referralCode,
          subdomain: args.subdomain
        })) as ({
          payload: {
            vendorId: string,
          },
        });

        if (result?.payload?.vendorId) { // if the vendor exists, redirect to / (dashboard route)
          redirect('/');
        } else { // otherwise, if vendor does not exist, redirect to onboarding
          redirect('/onboarding/business-info');
        }
      }
    });

    await cognitoIdentityProvider.signUp(
      args.email,
      args.password,
      args.trackingPartnerId ? [{ Name: 'custom:trackingPartnerId', Value: args.trackingPartnerId.toString() }] : undefined,
    );
  },
);

export const signOut = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'auth/signOut',
  async (_, { dispatch, getState }): Promise<void> => {
    localStorage.removeItem('persist:root');
    localStorage.removeItem('persist:vendor');

    const { promoCode, partnerId } = getState().onboarding;

    if (promoCode) {
      removeAnalyticsGroup('promoCode', promoCode as string);
    }
    if (partnerId) {
      removeAnalyticsGroup('partner', partnerId as number);
    }

    reset();

    const email = getState().auth.email;
    const vendorId = getState().auth.vendorId;

    if (email && vendorId) {
      await dispatch(vendorApi.endpoints.logUserAccountActivity.initiate({ email, activity: UserAccountActivityType.AuthSignOut })).catch(() => { /* */ });
    }

    dispatch({ type: 'SIGN_OUT' });

    await cognitoIdentityProvider.signOut();
  },
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (args: ChangePasswordArgs): Promise<void> => {
    await cognitoIdentityProvider.changePassword(args.oldPassword, args.newPassword);
  },
);

export const getMfaQrCodeUrl = createAsyncThunk(
  'auth/getMfaQrCodeUrl',
  async (args: { email: string }): Promise<{ qrCodeUrl: string }> => {
    const response = await cognitoIdentityProvider.associateSoftwareToken();
    const otpUrl = `otpauth://totp/${args.email}?secret=${response}&issuer=Mesa`;
    const qrCodeUrl = await new Promise<string>((resolve) => QRCode.toDataURL(otpUrl, (err: any, data_url: string) => {
      resolve(data_url);
    }));
    return { qrCodeUrl };
  },
);

export const updateMfaPreference = createAsyncThunk(
  'auth/updateMfaPreference',
  async (args: { code: string, mfaMethod: string }): Promise<{ mfaMethod: MfaMethod }> => {
    await cognitoIdentityProvider.verifySoftwareToken(args.code, 'SOFTWARE_TOKEN_MFA');
    await cognitoIdentityProvider.setUserMFAPreference();
    const user = cognitoIdentityProvider.getCurrentUser();
    if (user) {
      await cognitoIdentityProvider.updateUserAttributes([{ Name: ATTRIBUTE_MFA_METHOD, Value: args.mfaMethod }]);
    }
    return { mfaMethod: args.mfaMethod as MfaMethod };
  },
);

export type RemoveMfaArgs = {
  readonly email: string;
  readonly password: string;
  readonly code: string;
};

/**
 * Re-verifies the user before removing MFA, then records the removal so the account's contacts
 * are alerted. The removal itself runs client-side against Cognito, so the activity log is
 * best-effort: never block or fail a completed removal on it.
 */
async function reauthenticateAndLogMfaRemoval(
  { email, password, code }: RemoveMfaArgs,
  dispatch: any,
  removeMfa: () => Promise<void>,
): Promise<void> {
  await cognitoIdentityProvider.reauthenticateWithMfa(email, password, () => Promise.resolve(code), () => Promise.resolve(code));

  await removeMfa();

  const user = cognitoIdentityProvider.getCurrentUser();
  if (user) {
    await cognitoIdentityProvider.deleteUserAttributes([ATTRIBUTE_MFA_METHOD]);
  }

  await dispatch(vendorApi.endpoints.logUserAccountActivity.initiate({
    email,
    activity: UserAccountActivityType.AuthMfaRemoved,
  })).catch(() => { /* */ });
}

export const removeSoftwareMfa = createAsyncThunk(
  'auth/removeSoftwareMfa',
  async (args: RemoveMfaArgs, { dispatch }): Promise<void> => {
    await reauthenticateAndLogMfaRemoval(args, dispatch, () => cognitoIdentityProvider.removeUserMFAPreference());
  },
);

export const removeSmsMfa = createAsyncThunk(
  'auth/removeSmsMfa',
  async (args: RemoveMfaArgs, { dispatch }): Promise<void> => {
    await reauthenticateAndLogMfaRemoval(args, dispatch, () => cognitoIdentityProvider.removeUserSMSPreference());
  },
);

export const sendSmsPhoneNumberVerificationCode = createAsyncThunk(
  'auth/sendSmsPhoneNumberVerificationCode',
  async (args: { phoneNumber: string }): Promise<{ phoneNumber: string }> => {
    // Associate phone number with user for SMS MFA
    const user = cognitoIdentityProvider.getCurrentUser();
    if (user) {
      await cognitoIdentityProvider.updateUserAttributes([
        { Name: 'phone_number', Value: args.phoneNumber }
      ]);

      // Send verification code to the phone number
      await cognitoIdentityProvider.getPhoneNumberVerificationCode();
    }
    return { phoneNumber: args.phoneNumber };
  },
);

export const updateSmsMfaPreference = createAsyncThunk(
  'auth/updateSmsMfaPreference',
  async (args: { code: string, phoneNumber: string }): Promise<{ mfaMethod: MfaMethod }> => {
    // Verify the SMS code first
    await cognitoIdentityProvider.verifyPhoneNumber(args.code);

    // Set up SMS MFA preference
    await cognitoIdentityProvider.setUserSMSPreference();
    const user = cognitoIdentityProvider.getCurrentUser();
    if (user) {
      await cognitoIdentityProvider.updateUserAttributes([
        { Name: ATTRIBUTE_MFA_METHOD, Value: MfaMethod.SMS }
      ]);
    }
    return { mfaMethod: MfaMethod.SMS };
  },
);

export const setupCognito = createAsyncThunk<
  {
    externalVendorId?: string,
    email?: string,
    vendorId?: string,
    mfaMethod?: MfaMethod,
    roles: AuthenticationGroup[];
    isReadOnly: boolean;
    trackingPartnerId?: string;
    isDemoMode: boolean;
  },
  void,
  { state: RootState }
>(
  'auth/setupCognito',
  async (_, { getState }) => {
    let externalVendorId: string | undefined;
    let email: string | undefined;
    let vendorId: string | undefined;
    let mfaMethod: MfaMethod | undefined;
    let roles: AuthenticationGroup[] = [];
    let isReadOnly = true;
    let trackingPartnerId: string | undefined;
    const isDemoMode = getState().demoMode.isEnabled;

    const currentSession = cognitoIdentityProvider.getCurrentSession();
    const session = currentSession && !currentSession.isValid()
      ? await cognitoIdentityProvider.refreshCurrentSession()
      : currentSession;

    if (session?.isValid()) {
      const user = cognitoIdentityProvider.getCurrentUser();
      const attributes = user?.getAttributes() || {};
      const jwtTokenPayload = session.getIdToken().payload || {};

      mfaMethod = attributes[ATTRIBUTE_MFA_METHOD] as MfaMethod;
      roles = (jwtTokenPayload['cognito:groups'] as AuthenticationGroup[] | undefined) || [];
      if (attributes.email_verified) {
        externalVendorId = (attributes['custom:externalVendorId'] || attributes['custom:e1VendorNumber']) as string | undefined;
        email = attributes.email as string | undefined;
        vendorId = attributes['custom:vendorId'] as string | undefined;
        isReadOnly = attributes['custom:readOnly'] === 'true';
        trackingPartnerId = attributes['custom:trackingPartnerId'] as string | undefined;
      }
    }

    return {
      externalVendorId,
      email,
      vendorId,
      mfaMethod,
      roles,
      isReadOnly,
      trackingPartnerId,
      isDemoMode,
    };
  },
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (args: { email: string }): Promise<void> => {
    await cognitoIdentityProvider.forgotPassword(args.email);
  },
);

export const forgotPasswordSubmit = createAsyncThunk(
  'auth/forgotPasswordSubmit',
  async (args: { email: string, code: string, password: string }): Promise<void> => {
    await cognitoIdentityProvider.forgotPasswordSubmit(args.email, args.code, args.password);
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateEmail(state: AuthState, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    updateMfaCode(state: AuthState, action: PayloadAction<string>) {
      state.mfaCode = action.payload;
    },
    updateSmsCode(state: AuthState, action: PayloadAction<string>) {
      state.smsCode = action.payload;
    },
    updateIsLoggedIn(state: AuthState, action: PayloadAction<boolean>) {
      state.isLoggedIn = action.payload;
    },
    updateExternalVendorId(state: AuthState, action: PayloadAction<string>) {
      state.externalVendorId = action.payload;
    },
    updateTimeout429Timestamp(state: AuthState, action: PayloadAction<number>) {
      state.timeout429Timestamp = action.payload;
    },
    clearError(state: AuthState) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupCognito.fulfilled, (state: AuthState, action) => {
        state.email = action.payload.email;
        state.externalVendorId = action.payload.externalVendorId ?? state.externalVendorId;
        state.vendorId = action.payload.vendorId;
        state.isLoggedIn = !!action.payload.email;
        state.mfaMethod = action.payload.mfaMethod;
        state.roles = action.payload.roles;
        state.isReadOnly = action.payload.isReadOnly;
      })
      .addCase(setupCognito.rejected, (state: AuthState) => {
        state.isLoggedIn = false;
      })
      .addCase(signIn.fulfilled, (state: AuthState) => {
        state.isLoggedIn = true;
      })
      .addCase(signIn.rejected, (state: AuthState) => {
        state.isLoggedIn = false;
      })
      .addCase(signUp.fulfilled, (state: AuthState, action) => {
        state.email = action.meta.arg.email;
      })
      .addCase(signOut.fulfilled, (state: AuthState) => {
        state.email = '';
        state.isLoggedIn = false;
      })
      .addCase(postSignIn.fulfilled, (state: AuthState, action) => {
        state.isLoggedIn = true;
        state.vendorId = action.payload.vendorId;
        state.mfaMethod = action.payload.mfaMethod;
        state.isReadOnly = action.payload.isReadOnly;
      })
      .addCase(updateMfaPreference.fulfilled, (state: AuthState, action) => {
        state.mfaMethod = action.payload.mfaMethod;
      })
      .addCase(removeSoftwareMfa.fulfilled, (state: AuthState) => {
        state.mfaMethod = undefined;
      })
      .addCase(removeSmsMfa.fulfilled, (state: AuthState) => {
        state.mfaMethod = undefined;
      })
      .addCase(getMfaQrCodeUrl.fulfilled, (state: AuthState, action) => {
        state.qrCodeUrl = action.payload.qrCodeUrl;
      })
      .addCase(sendSmsPhoneNumberVerificationCode.fulfilled, (state: AuthState, action) => {
        state.phoneNumber = action.payload.phoneNumber;
      })
      .addCase(updateSmsMfaPreference.fulfilled, (state: AuthState, action) => {
        state.mfaMethod = action.payload.mfaMethod;
      })
      .addMatcher((action) => types.isPendingAction(action, 'auth'), (state: AuthState) => {
        state.loading = true;
      })
      .addMatcher((action) => types.isRejectedAction(action, 'auth'), (state: AuthState, action: any) => {
        state.loading = false;

        if (!action.payload) {
          state.error = action.error;
        }
      })
      .addMatcher((action) => types.isFulfilledAction(action, 'auth'), (state: AuthState) => {
        state.loading = false;
        state.error = undefined;
      });
  },
});

export const {
  clearError,
  updateEmail,
  updateMfaCode,
  updateSmsCode,
  updateIsLoggedIn,
  updateExternalVendorId,
  updateTimeout429Timestamp,
} = authSlice.actions;

export default authSlice.reducer;
